import { useState, useCallback, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  Filter,
  ChevronDown,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import {
  type FilterCondition,
  type FilterOperator,
} from '@/lib/api/odata'

export interface ColumnDef<T> {
  id: string
  header: string
  accessor: keyof T | ((row: T) => React.ReactNode)
  sortable?: boolean
  filterable?: boolean
  filterType?: 'text' | 'select' | 'boolean' | 'date' | 'number'
  filterOptions?: { label: string; value: string }[]
  width?: string
  cell?: (row: T) => React.ReactNode
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[]
  data: T[]
  totalCount?: number
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  // Pagination
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizeOptions?: number[]
  // Sorting
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSortChange: (field: string, direction: 'asc' | 'desc') => void
  // Filtering
  filters: Record<string, FilterCondition>
  onFilterChange: (filters: Record<string, FilterCondition>) => void
  // Search
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  // Toolbar actions (extra components shown near search)
  toolbarActions?: React.ReactNode
  // Actions
  rowKey: keyof T
  onRowClick?: (row: T) => void
  emptyMessage?: string
  // Hide toolbar when custom toolbar is provided externally
  hideToolbar?: boolean
}

export function DataTable<T extends { id: string }>({
  columns,
  data,
  totalCount = 0,
  loading = false,
  error = null,
  onRetry,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  sortField,
  sortDirection,
  onSortChange,
  filters,
  onFilterChange,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  toolbarActions,
  rowKey,
  onRowClick,
  emptyMessage = 'No data found',
  hideToolbar = false,
}: DataTableProps<T>) {
  const [localSearch, setLocalSearch] = useState(searchValue)

  const totalPages = Math.ceil(totalCount / pageSize)

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearchChange && localSearch !== searchValue) {
        onSearchChange(localSearch)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [localSearch, onSearchChange, searchValue])

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        onSortChange(field, sortDirection === 'asc' ? 'desc' : 'asc')
      } else {
        onSortChange(field, 'asc')
      }
    },
    [sortField, sortDirection, onSortChange]
  )

  const handleFilterChange = useCallback(
    (field: string, value: string, operator: FilterOperator = 'contains') => {
      const newFilters = { ...filters }
      if (value === '' || value === null || value === undefined) {
        delete newFilters[field]
      } else {
        newFilters[field] = { field, operator, value }
      }
      onFilterChange(newFilters)
    },
    [filters, onFilterChange]
  )

  const clearFilters = useCallback(() => {
    onFilterChange({})
    setLocalSearch('')
    if (onSearchChange) {
      onSearchChange('')
    }
  }, [onFilterChange, onSearchChange])

  const hasActiveFilters = Object.keys(filters).length > 0 || localSearch !== ''

  const renderSortIcon = (columnId: string) => {
    if (sortField !== columnId) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    )
  }

  const getCellValue = (row: T, column: ColumnDef<T>): React.ReactNode => {
    if (column.cell) {
      return column.cell(row)
    }
    if (typeof column.accessor === 'function') {
      return column.accessor(row)
    }
    return row[column.accessor] as React.ReactNode
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive mb-4">{error}</p>
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    )
  }

  const filterableColumns = columns.filter((col) => col.filterable)
  const activeFilterCount = Object.keys(filters).length

  return (
    <div className="space-y-4">
      {/* MS-Style Search and Filter Bar */}
      {!hideToolbar && (
      <div className="flex items-center gap-3">
        {/* Combined Search Input */}
        {onSearchChange && (
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="pl-9 pr-9 h-10 rounded-lg border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
            {localSearch && (
              <button
                onClick={() => setLocalSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Custom Toolbar Actions */}
        {toolbarActions}

        {/* Filter Button with Dropdown */}
        {filterableColumns.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-10 gap-2 rounded-lg border-gray-200 dark:border-gray-700"
              >
                <Filter className="h-4 w-4" />
                Filter
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80 p-4" align="start">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Filters</h4>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-xs">
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  {filterableColumns.map((column) => (
                    <div key={column.id} className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">{column.header}</label>
                      {column.filterType === 'select' && column.filterOptions ? (
                        <Select
                          value={(filters[column.id]?.value as string) || '__all__'}
                          onValueChange={(value) =>
                            handleFilterChange(column.id, value === '__all__' ? '' : value, 'eq')
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={`All ${column.header}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All</SelectItem>
                            {column.filterOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : column.filterType === 'boolean' ? (
                        <Select
                          value={
                            filters[column.id]?.value !== undefined
                              ? String(filters[column.id]?.value)
                              : '__all__'
                          }
                          onValueChange={(value) =>
                            handleFilterChange(
                              column.id,
                              value === '__all__' ? '' : value,
                              'eq'
                            )
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={`All ${column.header}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All</SelectItem>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          placeholder={`Filter by ${column.header.toLowerCase()}...`}
                          value={(filters[column.id]?.value as string) || ''}
                          onChange={(e) =>
                            handleFilterChange(column.id, e.target.value, 'contains')
                          }
                          className="h-9"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Results Count */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
          <span className="font-medium text-foreground">{totalCount.toLocaleString()}</span>
          <span>{totalCount === 1 ? 'result' : 'results'}</span>
        </div>
      </div>
      )}

      {/* Active Filters Pills */}
      {!hideToolbar && hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(filters).map(([field, condition]) => {
            const column = columns.find((c) => c.id === field)
            let displayValue = String(condition.value)
            if (condition.value === 'true') displayValue = 'Yes'
            if (condition.value === 'false') displayValue = 'No'
            return (
              <Badge
                key={field}
                variant="secondary"
                className="gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-normal"
              >
                <span className="text-muted-foreground">{column?.header}:</span>
                <span>{displayValue}</span>
                <button
                  onClick={() => handleFilterChange(field, '', 'eq')}
                  className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
          {localSearch && (
            <Badge
              variant="secondary"
              className="gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-normal"
            >
              <span className="text-muted-foreground">Search:</span>
              <span>"{localSearch}"</span>
              <button
                onClick={() => setLocalSearch('')}
                className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 px-2 text-xs">
            Clear all
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  style={{ width: column.width }}
                  className={column.sortable ? 'cursor-pointer select-none' : ''}
                  onClick={
                    column.sortable ? () => handleSort(column.id) : undefined
                  }
                >
                  <div className="flex items-center">
                    {column.header}
                    {column.sortable && renderSortIcon(column.id)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
              // Handle both camelCase and PascalCase keys from backend
              const keyStr = String(rowKey)
              const pascalKey = keyStr.charAt(0).toUpperCase() + keyStr.slice(1)
              const rowKeyValue = row[rowKey] ?? row[pascalKey as keyof T] ?? index
              return (
                <TableRow
                  key={String(rowKeyValue)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      {getCellValue(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              onPageSizeChange(Number(value))
              onPageChange(1) // Reset to first page when changing page size
            }}
          >
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages || 1}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(1)}
              disabled={page <= 1 || loading}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || loading}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || loading}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(totalPages)}
              disabled={page >= totalPages || loading}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
